import { TickMarkIcon } from '@sketchnote/icons';
import { GridApi } from 'ag-grid-community';
import { useRouter } from 'next/router';
import { memo, useEffect, useRef, useState } from 'react';
import { useTableStore } from 'src/features/list/store';
import useOnClickOutside from 'use-onclickoutside';
import { v4 as uuid } from 'uuid';

export const TableColor = {
  '#fbb6ce': '#EC7DD5',
  '#d6bcfa': '#18B4E6',
  '#81e6d9': '#81e6d9',
  '#86E495': '#7AA00D',
  '#9BADEE': '#211650',
  '#fbd38d': '#FD9740',
  '#FFE98F': '#FFE98F',
  '#FBB1A7': '#FBB1A7',
};

const randomColor = [
  '#fbb6ce',
  '#d6bcfa',
  '#9BADEE',
  '#81e6d9',
  '#86E495',
  '#FFE98F',
  '#fbd38d',
  '#FBB1A7',
];

const LabelCell = ({
  data: item,
  api,
  onChange,
}: {
  data: any;
  api: GridApi;
  onChange?: (item, value) => void;
}) => {
  const { onUpdateItemField, setItem, fetchLabel } = useTableStore((s) => ({
    onUpdateItemField: s.onUpdateItemField,
    setItem: s.setItem,
    fetchLabel: s.fetchLabel,
  }));
  const myRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputLabel, setInputLabel] = useState<string>('');
  const [labelOptions, setLabelOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  // const [isFetch, setFetch] = useState<boolean>(false);
  const { labels } = useTableStore((s) => ({
    labels: s.labels,
  }));
  const router = useRouter();
  const { isReadOnly } = useTableStore();

  useEffect(() => {
    const newLabels = labels?.map((label) => {
      return {
        id: label.id,
        label: label.label,
        isChecked: item?.label?.some((itemLabel) => itemLabel.id === label.id),
        color: label.color,
      };
    });

    setLabelOptions(newLabels);
  }, [labels]);

  useEffect(() => {
    setFilteredOptions(labelOptions);
  }, [labelOptions]);

  // useEffect(() => {
  //   if (isFetch) {
  //     fetchLabel({ id: router?.query?.view as string });
  //     setFetch(false);
  //   }
  // }, [isFetch]);

  useEffect(() => {
    if (inputLabel.trim() !== '') {
      const newLabelOptions = labelOptions.filter((labelOption) =>
        labelOption.label.includes(inputLabel)
      );
      setFilteredOptions(newLabelOptions);
    } else {
      setFilteredOptions(labelOptions);
    }
  }, [inputLabel]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleAddLabel = async () => {
    let updatedLabels: any[] = [];
    if (item.label && item.label.length) {
      updatedLabels = item?.label.map((item) => ({
        label: item.label,
        color: item.color,
        id: item.id,
      }));
    }
    const newLabel = {
      label: inputLabel,
      color: randomColor[Math.floor(Math.random() * randomColor.length)],
      id: uuid(),
    };
    updatedLabels.push(newLabel);
    onChange?.(item, updatedLabels);
    setItem({
      columnId: item.isSubtask ? item.parentColumn : item.colId,
      itemId: item.id,
      item: { ...item, label: updatedLabels },
    });
    await onUpdateItemField({
      item: {
        label: updatedLabels,
        id: item?.id || '',
      },
      keys: ['label'],
    });
    setInputLabel('');

    api.applyTransaction({ update: [{ ...item, label: updatedLabels }] });

    // setFetch(true);
    fetchLabel({ id: router?.query?.view as string });
  };

  const handleLabel = async (value) => {
    const updatedLabels = item?.label ? [...item.label] : [];
    const labelIds = item?.label ? item.label.map((updatedLabel) => updatedLabel.id) : [];
    const index = labelIds.indexOf(value.id);
    const labelIndex = labelOptions.map((labelObj) => labelObj.id).indexOf(value.id);
    if (index > -1) {
      updatedLabels.splice(index, 1);
    } else {
      updatedLabels.push({
        label: value.label,
        color: value.color,
        id: value.id,
      });
    }

    setInputLabel('');

    setItem({
      columnId: item.isSubtask ? item.parentColumn : item.colId,
      itemId: item.id,
      item: { ...item, label: updatedLabels },
    });
    const newLabelOptions = [...labelOptions];
    labelOptions[labelIndex].isChecked = !labelOptions[labelIndex].isChecked;
    setLabelOptions(newLabelOptions);
    await onUpdateItemField({
      item: {
        label: updatedLabels,
        id: item?.id || '',
      },
      keys: ['label'],
    });
  };

  useOnClickOutside(myRef, () => {
    api.stopEditing();
  });

  if (!item) return <div />;

  if (isReadOnly) return <div />;

  return (
    <div className="more-menu-popover" ref={myRef}>
      <div>
        <div
          className="w-275 p-16 rounded-lg z-100 bg-white"
          style={{ boxShadow: '0px 2px 16px rgba(19, 23, 32, 0.12)' }}
        >
          <input
            value={inputLabel}
            className="bg-gray-100 p-10 text-sm w-full"
            placeholder="Select a label or create one"
            onChange={(e) => setInputLabel(e.target.value)}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (filteredOptions.length > 0) {
                  handleLabel(filteredOptions[0]);
                } else if (inputLabel.trim() !== '') {
                  handleAddLabel();
                }
              }
            }}
          />

          <div className="max-h-384 overflow-y-scroll mt-16">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((labelOption) => {
                return (
                  <div
                    style={{ lineHeight: '10px' }}
                    key={`${labelOption.id}`}
                    onClick={handleLabel.bind(null, labelOption)}
                    className="  w-full py-4 font-semibold text-white rounded-sm px-6"
                  >
                    <button className="flex justify-between w-full ">
                      <span
                        className="inline-block px-6 rounded-sm text-xs py-4"
                        style={{ backgroundColor: TableColor[labelOption.color] }}
                      >
                        {labelOption.label}
                      </span>

                      {labelOption.isChecked && <TickMarkIcon className="mt-4 text-blue-600" />}
                    </button>
                  </div>
                );
              })
            ) : inputLabel.trim() === '' ? (
              <div className="p-8 text-sm text-gray-600">No labels created yet</div>
            ) : (
              <div
                className="cursor-pointer hover:text-gray-700 p-8 text-sm text-gray-600"
                onClick={handleAddLabel}
              >
                Create label {inputLabel}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const LabelCellEditor = memo(LabelCell);
